import { MAP_MILESTONE_DEFINITIONS, WON_HANDOFF_ITEM_DEFINITIONS } from '@/constants/map-milestones';
import type {
  DealMapMilestone,
  DealWonHandoffItem,
  MapMilestoneKey,
  MapMilestoneStatus,
  WonHandoffItemKey,
  WonHandoffItemStatus,
} from '@/types/deal-coordination';

export type DealMapMilestoneRow = {
  id: string;
  deal_id: string;
  milestone_key: MapMilestoneKey;
  status: MapMilestoneStatus;
  owner: string | null;
  target_date: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
};

export type DealWonHandoffItemRow = {
  id: string;
  deal_id: string;
  item_key: WonHandoffItemKey;
  status: WonHandoffItemStatus;
  notes: string;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

export function mergeDealMapMilestones(dealId: string, rows: DealMapMilestoneRow[]): DealMapMilestone[] {
  const rowByKey = new Map(rows.map((row) => [row.milestone_key, row]));

  return MAP_MILESTONE_DEFINITIONS.map((definition) => {
    const row = rowByKey.get(definition.milestoneKey);
    return {
      id: row?.id ?? null,
      deal_id: dealId,
      milestone_key: definition.milestoneKey,
      status: row?.status ?? 'not_started',
      owner: row?.owner ?? definition.defaultOwner,
      target_date: row?.target_date ?? null,
      notes: row?.notes ?? '',
      label: definition.label,
      gateId: definition.gateId,
      defaultOwner: definition.defaultOwner,
      blocksInstallNote: definition.blocksInstallNote,
      sortOrder: definition.sortOrder,
      created_at: row?.created_at ?? null,
      updated_at: row?.updated_at ?? null,
    };
  });
}

export function mergeDealWonHandoffItems(dealId: string, rows: DealWonHandoffItemRow[]): DealWonHandoffItem[] {
  const rowByKey = new Map(rows.map((row) => [row.item_key, row]));

  return WON_HANDOFF_ITEM_DEFINITIONS.map((definition) => {
    const row = rowByKey.get(definition.itemKey);
    return {
      id: row?.id ?? null,
      deal_id: dealId,
      item_key: definition.itemKey,
      status: row?.status ?? 'not_started',
      notes: row?.notes ?? '',
      completed_at: row?.completed_at ?? null,
      label: definition.label,
      description: definition.description,
      created_at: row?.created_at ?? null,
      updated_at: row?.updated_at ?? null,
    };
  });
}

export function computeMapProgress(milestones: DealMapMilestone[]): {
  completeCount: number;
  totalCount: number;
  percent: number;
} {
  const totalCount = milestones.length;
  const completeCount = milestones.filter(
    (milestone) => milestone.status === 'complete' || milestone.status === 'na'
  ).length;
  return {
    completeCount,
    totalCount,
    percent: totalCount === 0 ? 0 : Math.round((completeCount / totalCount) * 100),
  };
}

export function resolveHandoffCompletedAt(
  status: WonHandoffItemStatus,
  previousCompletedAt: string | null
): string | null {
  if (status === 'complete') {
    return previousCompletedAt ?? new Date().toISOString();
  }
  return null;
}

export function deriveHandoffStatuses(input: {
  locationId: string | null;
  csOwner: string | null;
}): Partial<Record<WonHandoffItemKey, WonHandoffItemStatus>> {
  const derived: Partial<Record<WonHandoffItemKey, WonHandoffItemStatus>> = {};

  if (input.locationId) {
    derived.location_linked = 'complete';
  }
  if (input.csOwner?.trim()) {
    derived.cs_owner_assigned = 'complete';
  }

  return derived;
}
